// ─────────────────────────────────────────────────────────────────────────────
// Sample Result Trend — empty-state wording.
//
// The backend classifies WHY a client (Site/Project) trend has no plottable
// points (AnalysisController .../trend/empty-reason → TrendReader →
// TrendEmptyReason.Classify, ONE source of truth). This module is ONLY the
// reviewer-facing sentence for each code, kept pure + unit-tested so the wording
// can't drift from the codes. Shared across every form that uses the Trend popup
// (standing rule 8 — the trend engine is one shared component).
// ─────────────────────────────────────────────────────────────────────────────

/** The empty-reason codes the API returns (mirrors `TrendEmptyReason` in C#). */
export type TrendEmptyReasonCode =
  | 'no_results'
  | 'pending_validation'
  | 'not_reportable'
  | 'non_detect_only'
  | 'no_collect_date'
  | 'out_of_window'
  | 'no_reviewed_history';

/**
 * The message shown in the empty client-trend panel.
 *
 * @param reason    the code from the API (unknown/missing → the generic fallback).
 * @param groupNoun "Site" or "Project" — how this panel is grouped.
 * @param months    the rolling-window length, for the out-of-window wording.
 */
export function trendEmptyMessage(
  reason: string | null | undefined,
  groupNoun: string,
  months: number,
): string {
  const g = (groupNoun || 'site').toLowerCase();
  switch (reason) {
    case 'no_results':
      return `No results for this ${g} and analyte yet.`;
    case 'pending_validation':
      return `Results for this ${g} and analyte are pending validation — the trend will populate once they clear review.`;
    case 'not_reportable':
      return `Reviewed results exist for this ${g} and analyte but none are marked reportable, so there is nothing to trend.`;
    case 'non_detect_only':
      return `All reviewed results for this ${g} and analyte are non-detects (< RL) — there is no detected value to plot.`;
    case 'no_collect_date':
      return `Reviewed results exist for this ${g} and analyte but have no collect date, so they can't be placed on the trend.`;
    case 'out_of_window':
      return `Reviewed results for this ${g} and analyte are older than the last ${months} months.`;
    case 'no_reviewed_history':
    default:
      return `No reviewed history for this ${g} and analyte.`;
  }
}
